import { createApi } from '@reduxjs/toolkit/query/react';
import { apiQuery, RtkQueryConfig } from 'core/rtkQuery';
import {
  ItemAssignmentsDto,
  ItemDetailsDto,
  ItemHistoryDto,
  MoveAssignmentDto,
  UpdateAssignmentDto,
} from 'apis/backendApi';
import {
  startPendingUpdate,
  syncPendingUpdates,
} from 'core/redux/pendingUpdatesSlice';
import { encodePendingAssignment } from 'views/Maintain/types';
import { OperationType } from 'core/commonTypes';
import { useCallback } from 'react';
import { addQueryContext, WithQueryContext } from 'core/apiQueryContext';

type AssignmentRequest = {
  itemNo: string;
  slid: string;
  date: string;
  body: UpdateAssignmentDto;
  lastUpdated: string;
};

const itemApiDef = createApi({
  reducerPath: 'itemApi',
  baseQuery: apiQuery(),
  tagTypes: ['assignments', 'history'],
  refetchOnMountOrArgChange: 5,

  endpoints: builder => ({
    getItemDetails: builder.query<
      ItemDetailsDto,
      WithQueryContext<{ itemNo: string }>
    >({
      query: ({ buCode, itemNo }) => ({
        url: '/items/v1/{buCode}/{itemNo}',
        pathVariables: {
          buCode,
          itemNo,
        },
      }),
    }),

    getItemAssignments: builder.query<
      ItemAssignmentsDto,
      WithQueryContext<{ itemNo: string }>
    >({
      query: ({ buCode, itemNo }) => ({
        url: '/items/v1/{buCode}/{itemNo}/assignments',
        pathVariables: {
          buCode,
          itemNo,
        },
      }),

      providesTags: (result, error, { itemNo }) => [
        { type: 'assignments', id: itemNo },
      ],

      async onQueryStarted({ buCode, itemNo }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const lastUpdated = Object.fromEntries(
            data.locations.flatMap(({ salesLocation, assignments }) =>
              assignments
                .flat(2)
                .filter(x => x !== null)
                .map(({ fromDate, lastUpdated }) => [
                  encodePendingAssignment({
                    buCode,
                    itemNo,
                    slid: salesLocation.id,
                    fromDate,
                  }),
                  lastUpdated,
                ])
            )
          );
          dispatch(syncPendingUpdates({ lastUpdated, prefix: 'assignment' }));
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }
      },
    }),

    postItemAssignment: builder.mutation<
      void,
      WithQueryContext<{
        itemNo: string;
        slid: string;
        date: string;
        fireClassOverride?: boolean;
        body: UpdateAssignmentDto;
      }>
    >({
      query: ({
        buCode,
        itemNo,
        slid,
        date,
        fireClassOverride = false,
        body,
      }) => ({
        method: 'POST',
        url: '/items/v1/{buCode}/{itemNo}/assignments/{slId}/{date}',
        pathVariables: { buCode, itemNo, slId: slid, date },
        params: { confirm: fireClassOverride },
        body,
      }),

      invalidatesTags: (result, error, { itemNo }) =>
        RtkQueryConfig.instance.isCloudEnabled
          ? [
              { type: 'assignments', id: itemNo },
              { type: 'history', id: itemNo },
            ]
          : [],

      async onQueryStarted(
        { buCode, itemNo, slid, date },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        if (RtkQueryConfig.instance.isMhsEnabled) {
          dispatch(
            startPendingUpdate({
              id: encodePendingAssignment({
                buCode,
                itemNo,
                slid,
                fromDate: date,
              }),
              type: OperationType.CREATE,
            })
          );
        }
      },
    }),

    postItemAssignmentMove: builder.mutation<
      void,
      WithQueryContext<{
        itemNo: string;
        fireClassOverride: boolean;
        body: MoveAssignmentDto;
      }>
    >({
      query: ({ buCode, itemNo, fireClassOverride, body }) => ({
        method: 'POST',
        url: '/items/v1/{buCode}/{itemNo}/assignments/move',
        pathVariables: { buCode, itemNo },
        params: { confirm: fireClassOverride },
        body,
      }),

      invalidatesTags: (_result, _error, { itemNo }) =>
        RtkQueryConfig.instance.isCloudEnabled
          ? [
              { type: 'assignments', id: itemNo },
              { type: 'history', id: itemNo },
            ]
          : [],

      /* @TODO: implement pending update after adding the move functionality to the integration layer  */
      // async onQueryStarted({ buCode, itemNo }, { dispatch, queryFulfilled }) {
      //   await queryFulfilled;

      //   // if (RtkQueryConfig.instance.isMhsEnabled) {
      //   //   dispatch(
      //   //     startPendingUpdate({
      //   //       id: encodePendingAssignment({
      //   //         buCode,
      //   //         itemNo,
      //   //         slid:,
      //   //         fromDate: date,
      //   //       }),
      //   //       type: OperationType.CREATE,
      //   //     })
      //   //   );
      //   // }
      // },
    }),

    // Cloud
    putItemAssignments: builder.mutation<
      void,
      WithQueryContext<{
        itemNo: string;
        slid: string;
        assignments: UpdateAssignmentDto[];
      }>
    >({
      query: ({ buCode, itemNo, slid, assignments }) => ({
        method: 'PUT',
        url: '/items/v1/{buCode}/{itemNo}/assignments/{slId}',
        pathVariables: { buCode, itemNo, slId: slid },
        body: {
          assignments,
        },
      }),

      invalidatesTags: (result, error, { itemNo }) =>
        RtkQueryConfig.instance.isCloudEnabled
          ? [
              { type: 'assignments', id: itemNo },
              { type: 'history', id: itemNo },
            ]
          : [],
    }),

    // MHS
    putItemAssignment: builder.mutation<
      void,
      WithQueryContext<AssignmentRequest>
    >({
      query: ({ buCode, itemNo, slid, date, body }) => ({
        method: 'PUT',
        url: '/items/v1/{buCode}/{itemNo}/assignments/{slId}/{date}',
        pathVariables: { buCode, itemNo, slId: slid, date },
        body,
      }),

      invalidatesTags: (result, error, { itemNo }) =>
        RtkQueryConfig.instance.isCloudEnabled
          ? [
              { type: 'assignments', id: itemNo },
              { type: 'history', id: itemNo },
            ]
          : [],

      async onQueryStarted(
        { buCode, itemNo, slid, date, lastUpdated },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        if (RtkQueryConfig.instance.isMhsEnabled) {
          dispatch(
            startPendingUpdate({
              id: encodePendingAssignment({
                buCode,
                itemNo,
                slid,
                fromDate: date,
              }),
              type: OperationType.UPDATE,
              lastUpdated,
            })
          );
        }
      },
    }),

    deleteItemAssignment: builder.mutation<
      void,
      WithQueryContext<{
        itemNo: string;
        slid: string;
        date: string;
        confirm?: boolean;
      }>
    >({
      query: ({ buCode, itemNo, slid, date, confirm }) => ({
        method: 'DELETE',
        url: '/items/v1/{buCode}/{itemNo}/assignments/{slId}/{date}',
        pathVariables: { buCode, itemNo, slId: slid, date },
        params: { confirm },
      }),

      invalidatesTags: (result, error, { itemNo }) =>
        RtkQueryConfig.instance.isCloudEnabled
          ? [
              { type: 'assignments', id: itemNo },
              { type: 'history', id: itemNo },
            ]
          : [],

      async onQueryStarted(
        { buCode, itemNo, slid, date },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        if (RtkQueryConfig.instance.isMhsEnabled) {
          dispatch(
            startPendingUpdate({
              id: encodePendingAssignment({
                buCode,
                itemNo,
                slid,
                fromDate: date,
              }),
              type: OperationType.DELETE,
            })
          );
        }
      },
    }),

    postItemCalculation: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string }>
    >({
      query: ({ buCode, itemNo }) => ({
        method: 'POST',
        url: '/items/v1/{buCode}/{itemNo}/calculation',
        pathVariables: { buCode, itemNo },
      }),
    }),

    getItemHistory: builder.query<
      ItemHistoryDto,
      WithQueryContext<{ itemNo: string }>
    >({
      query: ({ buCode, itemNo }) => ({
        url: '/items/v1/{buCode}/{itemNo}/history',
        pathVariables: {
          buCode,
          itemNo,
        },
      }),

      providesTags: (result, error, { itemNo }) => [
        { type: 'history', id: itemNo },
      ],
    }),

    putItemParameters: builder.mutation<
      void,
      WithQueryContext<{
        itemNo: string;
        flexibilityPercentage?: number;
        useCommercialQuantity?: boolean;
      }>
    >({
      query: ({
        buCode,
        itemNo,
        flexibilityPercentage,
        useCommercialQuantity,
      }) => ({
        method: 'PUT',
        url: '/items/v1/{buCode}/{itemNo}/parameters',
        pathVariables: { buCode, itemNo },
        body: {
          flexibilityPercentage,
          useCommercialQuantity,
        },
      }),

      async onQueryStarted(
        { buCode, itemNo, flexibilityPercentage, useCommercialQuantity },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        const { updateQueryData } = itemApi.util;
        dispatch(
          updateQueryData('getItemDetails', { buCode, itemNo }, draft => {
            if (flexibilityPercentage !== undefined) {
              draft.flexibilityPercentage = flexibilityPercentage;
            }
            if (useCommercialQuantity !== undefined) {
              draft.useCommercialQuantity = useCommercialQuantity;
            }
          })
        );
      },

      invalidatesTags: (result, error, { itemNo }) => [
        { type: 'history', id: itemNo },
      ],
    }),

    putItemNotifications: builder.mutation<
      void,
      WithQueryContext<{ itemNo: string; slid: string; handled: boolean }>
    >({
      query: ({ buCode, itemNo, slid, handled }) => ({
        method: 'PUT',
        url: '/items/v1/{buCode}/{itemNo}/notifications/{slId}',
        pathVariables: { buCode, itemNo, slId: slid },
        body: {
          handled,
        },
      }),

      async onQueryStarted(
        { buCode, itemNo, slid, handled },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
        } catch (e) {
          // Handled by caller. No throws in onQueryStarted as per RtkQuery
          return;
        }

        const { updateQueryData } = itemApi.util;
        dispatch(
          updateQueryData('getItemDetails', { buCode, itemNo }, draft => {
            if (handled) {
              draft.notifications = draft.notifications.filter(
                x => x.slid !== slid
              );
            }
          })
        );
      },
    }),
  }),
});

/**
 * Wrapper mutation for updating assignments.
 */
export function useUpdateAssignmentsMutation() {
  const [putItemAssignment, putItemAssignmentResult] =
    itemApi.usePutItemAssignmentMutation();
  const [putItemAssignments, putItemAssignmentsResult] =
    itemApi.usePutItemAssignmentsMutation();

  const mutation = useCallback(
    async (requests: AssignmentRequest[]) => {
      if (RtkQueryConfig.instance.isMhsEnabled) {
        for (const request of requests) {
          const update = async () => await putItemAssignment(request).unwrap();
          await update();
        }
      } else {
        // Cloud
        if (requests.length === 0) {
          return;
        }

        const [{ itemNo, slid }] = requests;
        const assignments = requests.map(x => ({ date: x.date, ...x.body }));
        await putItemAssignments({
          itemNo,
          slid,
          assignments,
        }).unwrap();
      }
    },
    [putItemAssignment, putItemAssignments]
  );

  // TypeScript cannot infer the correct type of the mutation when returning 2-element array, so return object instead.
  return {
    mutation,
    result: RtkQueryConfig.instance.isMhsEnabled
      ? putItemAssignmentResult
      : putItemAssignmentsResult,
  };
}

const itemApi = addQueryContext(itemApiDef);

export const {
  useGetItemDetailsQuery,
  useGetItemAssignmentsQuery,
  usePostItemAssignmentMutation,
  usePostItemAssignmentMoveMutation,
  useDeleteItemAssignmentMutation,
  usePostItemCalculationMutation,
  useGetItemHistoryQuery,
  usePutItemParametersMutation,
  usePutItemNotificationsMutation,
} = itemApi;
export default itemApi;
